import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  passwordResetRequestSchema,
  passwordResetSchema,
  type PasswordResetInput,
  type PasswordResetRequestInput,
} from '@/shared/lib/validations'
import { ApiError } from '@/shared/lib/api'
import { useAuthStore } from '@/shared/stores/auth'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { requestPasswordReset, resetPassword } from '../api'
import { useAuthDialogStore } from '../stores/auth-dialog'

type ResetStage = 'request' | 'reset'

export function ForgotPasswordDialog() {
  const { isOpen, close, switchView, onSuccess } = useAuthDialogStore()
  const { setToken, setUser } = useAuthStore()
  const [stage, setStage] = useState<ResetStage>('request')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestForm = useForm<PasswordResetRequestInput>({
    resolver: zodResolver(passwordResetRequestSchema),
  })
  const resetForm = useForm<PasswordResetInput>({
    resolver: zodResolver(passwordResetSchema),
  })

  const submitRequest = async (input: PasswordResetRequestInput) => {
    setIsLoading(true)
    setError(null)
    try {
      await requestPasswordReset(input)
      setEmail(input.email)
      setStage('reset')
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 400) {
        setEmail(input.email)
        setStage('reset')
      } else {
        setError(requestError instanceof Error ? requestError.message : 'Failed to request reset')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const submitReset = async (input: PasswordResetInput) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await resetPassword({
        email,
        password: input.password,
        resetToken: input.resetToken,
      })
      if (response.refreshToken) setToken(response.refreshToken)
      setUser(response.user)
      close()
      onSuccess?.()
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : 'Failed to reset password')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="!bottom-0 !left-0 !right-0 !top-auto !translate-x-0 !translate-y-0 rounded-none border-x-0 border-b-0 sm:!left-[50%] sm:!top-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:!bottom-auto sm:!right-auto sm:max-w-md sm:rounded-lg sm:border">
        <DialogHeader className="text-center">
          <DialogTitle>{stage === 'request' ? 'Forgot Password' : 'Enter Reset Code'}</DialogTitle>
          <DialogDescription>
            {stage === 'request'
              ? 'Enter your email to receive a password reset code.'
              : 'If an account exists for that email, we sent a reset code. Enter it below.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        {stage === 'request' ? (
          <form
            key="request"
            onSubmit={requestForm.handleSubmit(submitRequest)}
            className="mt-4 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="you@example.com"
                disabled={isLoading}
                {...requestForm.register('email')}
              />
              {requestForm.formState.errors.email && (
                <p className="text-sm text-destructive">
                  {requestForm.formState.errors.email.message}
                </p>
              )}
            </div>
            <Button type="submit" variant="gradient" className="w-full" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Code'}
            </Button>
          </form>
        ) : (
          <form
            key="reset"
            onSubmit={resetForm.handleSubmit(submitReset)}
            className="mt-4 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="reset-token">Reset Code</Label>
              <Input
                id="reset-token"
                placeholder="Reset code"
                autoComplete="one-time-code"
                disabled={isLoading}
                {...resetForm.register('resetToken')}
              />
              {resetForm.formState.errors.resetToken && (
                <p className="text-sm text-destructive">
                  {resetForm.formState.errors.resetToken.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-password">New Password</Label>
              <Input
                id="reset-password"
                type="password"
                placeholder="New password"
                autoComplete="new-password"
                disabled={isLoading}
                {...resetForm.register('password')}
              />
              {resetForm.formState.errors.password && (
                <p className="text-sm text-destructive">
                  {resetForm.formState.errors.password.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm-password">Confirm New Password</Label>
              <Input
                id="reset-confirm-password"
                type="password"
                placeholder="Confirm new password"
                autoComplete="new-password"
                disabled={isLoading}
                {...resetForm.register('confirmPassword')}
              />
              {resetForm.formState.errors.confirmPassword && (
                <p className="text-sm text-destructive">
                  {resetForm.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>
            <Button type="submit" variant="gradient" className="w-full" disabled={isLoading}>
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setStage('request')
                setEmail('')
                setError(null)
                requestForm.reset()
                resetForm.reset()
              }}
            >
              Use a Different Email
            </Button>
          </form>
        )}

        <button
          type="button"
          onClick={() => switchView('signin')}
          className="mt-2 cursor-pointer border-none bg-transparent p-0 text-center text-sm text-primary hover:underline"
        >
          Back to sign in
        </button>
      </DialogContent>
    </Dialog>
  )
}
